import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { call } from "./api";
import type { ActivityType, Booking, Participant, Plan, Balance, SessionWithRelations } from "./types";

// ─── Reference data ─────────────────────────────────────────────────────────

export function useActivityTypes() {
  return useQuery({
    queryKey: ["activity_types"],
    queryFn: () => call<ActivityType[]>("getActivityTypes"),
  });
}

export interface TrainerLite { id: string; name: string; is_active: boolean }

export function useTrainers() {
  return useQuery({
    queryKey: ["trainers"],
    queryFn: () => call<TrainerLite[]>("getTrainers"),
  });
}

export function usePlans() {
  return useQuery({
    queryKey: ["plans"],
    queryFn: () => call<Plan[]>("getPlans"),
  });
}

// ─── Sessions ───────────────────────────────────────────────────────────────

export function useUpcomingSessions() {
  return useQuery({
    queryKey: ["sessions", "feed"],
    queryFn: () => call<SessionWithRelations[]>("getFeed"),
  });
}

export function useSession(id: string | undefined) {
  return useQuery({
    queryKey: ["session", id],
    enabled: !!id,
    queryFn: () => call<SessionWithRelations>("getSession", { id: id! }),
  });
}

export function useTrainerSessions(_trainerId: string | undefined, scope: "mine" | "club" = "mine") {
  return useQuery({
    queryKey: ["sessions", "trainer", scope],
    queryFn: () => call<SessionWithRelations[]>("getTrainerSessions", { scope }),
  });
}

export function useCreateSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      trainer_id: string;
      activity_type_id: string;
      starts_at: string;
      ends_at: string | null;
      location_text: string | null;
      capacity: number;
      notes: string | null;
    }) => {
      // trainer_id is ignored server-side; current trainer used.
      const { trainer_id: _ignored, ...rest } = input;
      void _ignored;
      return call<{ id: string }>("createSession", rest);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sessions"] }),
  });
}

export function useUpdateSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; location_text?: string | null; notes?: string | null }) =>
      call("updateSession", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sessions"] });
      qc.invalidateQueries({ queryKey: ["session"] });
    },
  });
}

export function useCancelSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => call("cancelSession", { id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sessions"] }),
  });
}

// ─── Participants ───────────────────────────────────────────────────────────

export function useMyParticipants(_parentId: string | undefined) {
  return useQuery({
    queryKey: ["participants"],
    queryFn: () => call<Participant[]>("getMyParticipants"),
  });
}

export function useAddParticipant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { parent_id: string; name: string; birth_year?: number | null }) => {
      const { parent_id: _ignored, ...rest } = input;
      void _ignored;
      return call<Participant>("addParticipant", rest);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["participants"] }),
  });
}

export function useSetCoaches() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { participant_id: string; trainer_ids: string[] }) =>
      call("setCoaches", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["participants"] });
      qc.invalidateQueries({ queryKey: ["participant-detail"] });
      qc.invalidateQueries({ queryKey: ["roster"] });
      qc.invalidateQueries({ queryKey: ["sessions"] });
    },
  });
}

export function useAddCoach() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { participant_id: string }) => call("addCoach", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roster"] });
      qc.invalidateQueries({ queryKey: ["participant-detail"] });
      qc.invalidateQueries({ queryKey: ["sessions"] });
    },
  });
}

export function useRemoveCoach() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { participant_id: string; trainer_id: string }) =>
      call("removeCoach", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roster"] });
      qc.invalidateQueries({ queryKey: ["participant-detail"] });
      qc.invalidateQueries({ queryKey: ["sessions"] });
    },
  });
}

// ─── Bookings ───────────────────────────────────────────────────────────────

export function useCreateBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { session_id: string; participant_id: string; needs_skipass?: boolean }) =>
      call<Booking>("createBooking", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sessions"] });
      qc.invalidateQueries({ queryKey: ["session"] });
      qc.invalidateQueries({ queryKey: ["my-bookings"] });
      qc.invalidateQueries({ queryKey: ["history"] });
    },
  });
}

export function useCreateBookings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { session_id: string; kids: Array<{ participant_id: string; needs_skipass?: boolean }> }) =>
      call<Booking[]>("createBookings", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sessions"] });
      qc.invalidateQueries({ queryKey: ["session"] });
      qc.invalidateQueries({ queryKey: ["my-bookings"] });
      qc.invalidateQueries({ queryKey: ["history"] });
    },
  });
}

export function useUpdateBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { booking_id: string; needs_skipass?: boolean }) =>
      call("updateBooking", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sessions"] });
      qc.invalidateQueries({ queryKey: ["session"] });
    },
  });
}

export function useCancelBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bookingId: string) => call("cancelBooking", { booking_id: bookingId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sessions"] });
      qc.invalidateQueries({ queryKey: ["session"] });
      qc.invalidateQueries({ queryKey: ["my-bookings"] });
      qc.invalidateQueries({ queryKey: ["history"] });
    },
  });
}

export function useMarkAttendance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { booking_id: string; status: Booking["status"] }) =>
      call("markAttendance", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["session"] });
      qc.invalidateQueries({ queryKey: ["history"] });
      qc.invalidateQueries({ queryKey: ["participant-detail"] });
      qc.invalidateQueries({ queryKey: ["balances"] });
      qc.invalidateQueries({ queryKey: ["roster"] });
    },
  });
}

export function useMyBookings(_parentId: string | undefined) {
  return useQuery({
    queryKey: ["my-bookings"],
    queryFn: () => call<unknown[]>("getMyBookings"),
  });
}

export function useHistory(_parentId: string | undefined) {
  return useQuery({
    queryKey: ["history"],
    queryFn: () => call<unknown[]>("getHistory"),
  });
}

// ─── Balances ───────────────────────────────────────────────────────────────

export function useBalances(participantId: string | undefined) {
  return useQuery({
    queryKey: ["balances", participantId],
    enabled: !!participantId,
    queryFn: () => call<Balance[]>("getBalances", { participant_id: participantId! }),
  });
}

export function useParticipantDetail(participantId: string | undefined) {
  return useQuery({
    queryKey: ["participant-detail", participantId],
    enabled: !!participantId,
    queryFn: () => call<unknown>("getParticipantDetail", { participant_id: participantId! }),
  });
}

export function useAddBalance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { participant_id: string; plan_id: string; paid_amount?: number | null; note?: string | null }) =>
      call<Balance>("addBalance", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["balances"] });
      qc.invalidateQueries({ queryKey: ["participant-detail"] });
      qc.invalidateQueries({ queryKey: ["roster"] });
    },
  });
}

// ─── People (trainer view) ──────────────────────────────────────────────────

export function useClubPeople(_clubId: string | undefined) {
  return useQuery({
    queryKey: ["club-people"],
    queryFn: () => call<unknown[]>("getClubPeople"),
  });
}

export interface RosterItem {
  id: string;
  name: string;
  birth_year: number | null;
  coaches: Array<{ id: string; name: string; is_primary: boolean }>;
  parent_id: string;
  parent_name: string;
  parent_tg_id: number;
  balances: Array<{ slug: string; name: string; emoji: string | null; remaining: number }>;
  last_attended_at: string | null;
  attended_count: number;
  upcoming_count: number;
}

export function useCreateInvite() {
  return useMutation({
    mutationFn: (input: { role: "trainer" | "parent"; expires_in_days?: number }) =>
      call<{ token: string; expires_at: string; role: string }>("createInvite", input),
  });
}

export function useRoster() {
  return useQuery({
    queryKey: ["roster"],
    queryFn: () => call<RosterItem[]>("getRoster"),
  });
}

// Re-export call for components that need ad-hoc requests.
export { call };
